'use client';

import { useState } from 'react';

import { Button, Menu, MenuItem, Typography } from 'lighthouse';

import { PRODUCT_SORT_COPY } from '@/constants/productSort';

import { ProductSortBarProps } from './models';

import { Sort } from 'iconsax-react';

export const SortBar = <T extends string>({
    value,
    onChange,
    options,
    leading,
    className = ''
}: ProductSortBarProps<T> & { options: readonly { value: T; label: string }[] }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const selectedLabel = options.find((option) => option.value === value)?.label ?? options[0]?.label ?? '';
    const isMenuOpen = Boolean(anchorEl);

    return (
        <div
            className={`flex w-full items-center gap-1 rounded-lg border border-solid border-N100 bg-white px-1 py-1.5 md:gap-2 md:px-2 md:py-2 ${className}`.trim()}>
            {leading ? (
                <>
                    <div className='flex shrink-0 items-center md:hidden'>{leading}</div>
                    <span className='my-1 w-px shrink-0 self-stretch bg-N100 md:hidden' aria-hidden />
                </>
            ) : null}

            <div className='hidden min-w-0 flex-1 items-center gap-1 overflow-x-auto md:flex'>
                <span className='flex shrink-0 items-center gap-1.5 px-1'>
                    <Sort size={20} color='currentColor' className='text-blackLowEmphasis' />
                    <Typography variant='body2' component='span' color='blackMediumEmphasis' className='whitespace-nowrap font-bold'>
                        {PRODUCT_SORT_COPY.label}
                    </Typography>
                </span>
                {options.map((option) => {
                    const isActive = option.value === value;

                    return (
                        <Button
                            key={option.value}
                            variant='text'
                            color={isActive ? 'primary' : 'blackMediumEmphasis'}
                            className={`min-w-0 px-2 py-1 ${isActive ? 'font-bold' : ''}`.trim()}
                            onClick={() => onChange(option.value)}>
                            {option.label}
                        </Button>
                    );
                })}
            </div>

            <div className={`min-w-0 md:hidden ${leading ? 'ms-auto' : ''}`.trim()}>
                <Button
                    variant='text'
                    color='blackMediumEmphasis'
                    className='min-w-0 px-1 py-1'
                    aria-controls={isMenuOpen ? 'product-sort-menu' : undefined}
                    aria-haspopup='true'
                    aria-expanded={isMenuOpen ? 'true' : undefined}
                    onClick={(event) => setAnchorEl(event.currentTarget)}>
                    <span className='flex min-w-0 items-center gap-1.5'>
                        <Sort size={18} color='currentColor' className='shrink-0 text-blackLowEmphasis' />
                        <Typography variant='body2' component='span' color='blackMediumEmphasis' className='shrink-0 font-bold'>
                            {PRODUCT_SORT_COPY.label}
                        </Typography>
                        <Typography variant='body2' component='span' color='primary' className='truncate font-semibold'>
                            {selectedLabel}
                        </Typography>
                    </span>
                </Button>
                <Menu
                    id='product-sort-menu'
                    anchorEl={anchorEl}
                    open={isMenuOpen}
                    onClose={() => setAnchorEl(null)}
                    MenuListProps={{ 'aria-labelledby': 'product-sort-menu' }}>
                    {options.map((option) => (
                        <MenuItem
                            key={option.value}
                            selected={option.value === value}
                            onClick={() => {
                                onChange(option.value);
                                setAnchorEl(null);
                            }}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Menu>
            </div>
        </div>
    );
};
